import React from "react";
import {
  KintoneExportReport,
  KintoneExportSetting,
} from "@graffer-inc/cloud-gov-core";
import {
  gqlClient,
  gqlHeader,
  reportUnexpectedError,
  runGqlQueryV2,
} from "@graffer-inc/smart-apply";

export type UseInputPropsParam = {
  value: string;
  onChange: (v: string) => void;
  validate?: (v: string) => string | undefined;
};

export type UseInputPropsOutput = {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  errorMessages: string | undefined;
  onBlur: () => void;
};

export const useInputProps = ({
  value,
  onChange,
  validate,
}: UseInputPropsParam): UseInputPropsOutput => {
  const [errorMessages, setErrorMessages] = React.useState<string | undefined>(
    undefined,
  );
  const [touched, setTouched] = React.useState(false);

  const onChangeWrapped = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTouched(true);
    onChange(e.target.value);
  };

  React.useEffect(() => {
    if (!touched) {
      return;
    }
    if (typeof validate === "function") {
      const errorMessage = validate(value);
      setErrorMessages(errorMessage);
    }
  }, [touched, value, setErrorMessages, validate]);

  const onBlur = () => {
    setTouched(true);
  };

  return {
    value,
    onChange: onChangeWrapped,
    errorMessages,
    onBlur,
  };
};

export type ManualExportSubmissionInput = {
  settingId: KintoneExportSetting["id"];
  submissionIds: string[];
};

export type ManualExportSubmissionFuncType = (
  param: ManualExportSubmissionInput,
  callback: {
    onSucceeded: (report: Pick<KintoneExportReport, "id">) => void;
    onFailed: (errorMessage: string) => void;
    onPolingTimeout: () => void;
  },
) => void;

const pollingInterval = 5000;
const maxPollingCount = 120; // 120 * 5000ms = 600000ms = 10min

export const useManualExportSubmission = () => {
  const [newReport, setNewReport] =
    React.useState<Pick<KintoneExportReport, "id">>();

  const manualExportSubmission =
    React.useCallback<ManualExportSubmissionFuncType>(
      async (param, callback) => {
        try {
          const requestRes = await runGqlQueryV2(() => {
            return gqlClient.requestKintoneExport(
              {
                input: {
                  settingId: param.settingId,
                  submissionIds: param.submissionIds,
                },
              },
              gqlHeader("m"),
            );
          });

          if (requestRes.error) {
            reportUnexpectedError(requestRes.error.rawError);
            callback.onFailed("連携の実行に失敗しました。");
            return;
          }

          const kintoneExportRequestId =
            requestRes.data!.requestKintoneExport.id;

          let pollingCount = 0;
          while (true) {
            await new Promise((resolve) =>
              setTimeout(resolve, pollingInterval),
            );

            const res = await runGqlQueryV2(() => {
              return gqlClient.kintoneExportRequest(
                { id: kintoneExportRequestId },
                gqlHeader("q"),
              );
            });

            if (res.error) {
              reportUnexpectedError(res.error.rawError);
              callback.onFailed("連携の実行に失敗しました。");
              return;
            }

            switch (res.data!.kintoneExportRequest.status) {
              case "pending":
              case "running":
                pollingCount++;
                if (pollingCount >= maxPollingCount) {
                  callback.onPolingTimeout();
                  return;
                }
                continue;
              case "failed":
                callback.onFailed("連携の実行に失敗しました。");
                return;
              case "succeeded": {
                const report = {
                  id: res.data!.kintoneExportRequest.createdReportId,
                };
                setNewReport(report);
                callback.onSucceeded(report);
                return;
              }
            }
          }
        } catch (error) {
          reportUnexpectedError(error);
          callback.onFailed("連携の実行に失敗しました。");
        }
      },
      [setNewReport],
    );

  return {
    newReport,
    manualExportSubmission,
  };
};
